import { NavLink } from "@/components/NavLink";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Search, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";
import cartIcon from "@/assets/cart-icon.png";
import SearchAutocomplete from "@/components/SearchAutocomplete";

const Header = () => {
  const { items, removeFromCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<any>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  
  const totalPrice = items.reduce((sum, item) => sum + item.price, 0);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinks = [
    { to: "/saelg-skins", label: "Sælg Skins", matchPaths: ["/saelg-skins"] },
    { to: "/koeb-skins", label: "Køb Skins", matchPaths: ["/koeb-skins"] },
    { to: "/faq", label: "FAQ", matchPaths: ["/faq"] },
    { to: "/blogs", label: "Blogs", matchPaths: ["/blogs", "/blog"] },
    { to: "/kontakt-os", label: "Kontakt Os", matchPaths: ["/kontakt-os"] },
  ];

  const isLinkActive = (matchPaths: string[]) => {
    return matchPaths.some(path => location.pathname.startsWith(path));
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full bg-white border-b border-border">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <button 
          onClick={handleLogoClick}
          className="flex items-center hover:opacity-80 transition-opacity"
        >
          <img src={logo} alt="Ramborgh Invest" width={120} height={40} className="h-8 md:h-10 w-auto" />
        </button>

        {/* Desktop Search Bar with Autocomplete */}
        <div className="hidden md:flex flex-1 max-w-md mx-8">
          <SearchAutocomplete className="w-full" />
        </div>

        {/* Desktop Navigation Links */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <NavLink 
              key={link.to}
              to={link.to} 
              className={cn(
                "relative text-black transition-colors font-semibold text-sm after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-[#15171C] after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left",
                isLinkActive(link.matchPaths) ? "after:scale-x-100" : "after:scale-x-0"
              )}
            >
              {link.label}
            </NavLink>
          ))}

          {/* Auth Button */}
          <button 
            onClick={() => navigate(user ? "/account" : "/auth")}
            className="relative hover:opacity-80 transition-opacity"
          >
            <User className="w-6 h-6 text-black" />
          </button>
          
          {/* Cart Icon */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="relative ml-2 hover:opacity-80 transition-opacity">
                <img src={cartIcon} alt="Cart" width={24} height={24} className="w-6 h-6 brightness-0" />
                {items.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#1A1C23] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold border border-white">
                    {items.length}
                  </span>
                )}
              </button>
            </SheetTrigger>
            <SheetContent className="bg-[#1A1C23] border-l border-white">
              <SheetHeader>
                <SheetTitle className="text-white">Din Kurv</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                {items.length === 0 ? (
                  <p className="text-white text-center py-8">Din kurv er tom</p>
                ) : (
                  <>
                    {items.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg">
                        <img src={item.image} alt={item.name} width={64} height={64} loading="lazy" className="w-16 h-16 object-contain bg-gray-100 rounded" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-white">{item.name}</h4>
                          <p className="text-xs text-white/80">{item.description}</p>
                          <p className="text-sm font-bold text-white mt-1">
                            {item.price.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DKK
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeFromCart(item.id)}
                          className="hover:bg-primary/10 hover:text-primary text-white"
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex justify-between items-center mb-4">
                        <span className="font-semibold text-lg text-white">Total:</span>
                        <span className="font-bold text-xl text-white">
                          {totalPrice.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DKK
                        </span>
                      </div>
                      <Button 
                        className="w-full bg-[#1A1C23] text-white border border-white hover:bg-[#1A1C23]/80 hover:scale-[1.02] transition-all" 
                        onClick={() => navigate("/checkout")}
                      >
                        Fortsæt til betaling
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </nav>

        {/* Mobile/Tablet Navigation Icons */}
        <div className="flex lg:hidden items-center gap-2">
          {/* Mobile Search Toggle */}
          <button 
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
            className="p-2 hover:opacity-80 transition-opacity"
            aria-label="Søg"
          >
            <Search className="w-5 h-5 text-black" />
          </button>

          {/* Auth Button - Mobile */}
          <button 
            onClick={() => navigate(user ? "/account" : "/auth")}
            className="p-2 hover:opacity-80 transition-opacity"
            aria-label={user ? "Min konto" : "Log ind"}
          >
            <User className="w-5 h-5 text-black" />
          </button>
          
          {/* Cart Icon - Mobile */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="relative p-2 hover:opacity-80 transition-opacity" aria-label="Indkøbskurv">
                <img src={cartIcon} alt="Cart" width={20} height={20} className="w-5 h-5 brightness-0" />
                {items.length > 0 && (
                  <span className="absolute top-0 right-0 bg-[#1A1C23] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-semibold border border-white text-[10px]">
                    {items.length}
                  </span>
                )}
              </button>
            </SheetTrigger>
            <SheetContent className="bg-[#1A1C23] border-l border-white">
              <SheetHeader>
                <SheetTitle className="text-white">Din Kurv</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                {items.length === 0 ? (
                  <p className="text-white text-center py-8">Din kurv er tom</p>
                ) : (
                  <>
                    {items.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg">
                        <img src={item.image} alt={item.name} width={64} height={64} loading="lazy" className="w-16 h-16 object-contain bg-gray-100 rounded" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-white">{item.name}</h4>
                          <p className="text-xs text-white/80">{item.description}</p>
                          <p className="text-sm font-bold text-white mt-1">
                            {item.price.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DKK
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeFromCart(item.id)}
                          className="hover:bg-primary/10 hover:text-primary text-white"
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex justify-between items-center mb-4">
                        <span className="font-semibold text-lg text-white">Total:</span>
                        <span className="font-bold text-xl text-white">
                          {totalPrice.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DKK
                        </span>
                      </div>
                      <Button 
                        className="w-full bg-[#1A1C23] text-white border border-white hover:bg-[#1A1C23]/80 hover:scale-[1.02] transition-all" 
                        onClick={() => navigate("/checkout")}
                      >
                        Fortsæt til betaling
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* Mobile Menu Toggle */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:opacity-80 transition-opacity"
            aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-black" />
            ) : (
              <Menu className="w-6 h-6 text-black" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Search Bar - Expandable */}
      {mobileSearchOpen && (
        <div className="lg:hidden px-4 pb-4 bg-white">
          <SearchAutocomplete 
            className="w-full" 
            autoFocus 
            onClose={() => setMobileSearchOpen(false)} 
          />
        </div>
      )}

      {/* Mobile Menu - Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-border">
          <nav className="flex flex-col px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <NavLink 
                key={link.to}
                to={link.to} 
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "px-4 py-3 text-black font-semibold hover:bg-gray-100 rounded-lg transition-colors",
                  isLinkActive(link.matchPaths) && "bg-gray-100"
                )}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;